// FE-SCRM-056 + FE-PINV-074 — Apply playbook modal.
//
// Three modes:
//   single — existing FE-SCRM-056 surface; requires ``opportunity``
//   pick   — embeds <TargetSearchPicker> as the first field; computes
//            due dates against the picked opportunity's renewal anchor
//   bulk   — header "Apply to {n} opportunities"; preview shows
//            blocks WITHOUT computed due dates (each opportunity has
//            its own anchor); fan-out fires the per-op apply endpoint
//            sequentially and surfaces partial failures in a banner
//
// Single-mode pre-selection order (FE-SCRM-056 + FE-SCRM-055):
//   1. ``lockedPlaybookId`` (Re-apply flow locks the original choice).
//   2. The brokerage's pinned default
//      (``BrokerSettings.pinned_default_playbook_id``) when present.
//   3. The first row in the list (fallback).
// Pinned-default pre-selection applies in all three modes.

import {
  useCallback, useContext, useEffect, useMemo, useState,
} from "react";

import { Button, Modal } from "@/design-system";
import { AuthContext } from "@/features/auth/AuthContext";
import { useBrokerSettingsQuery } from "@/features/settings/hooks";

import {
  TargetSearchPicker,
  type TargetSearchResult,
} from "../components/TargetSearchPicker";
import { CrmFormBanner } from "../components/CrmFormBanner";
import { useOpportunityDetailQuery } from "../OpportunityDetail/hooks";

import { computeDueDate, durationWindowLabel } from "./dueDate";
import {
  useApplyPlaybookForOpportunity,
  useApplyPlaybookMutation,
  usePlaybookDetailQuery,
  usePlaybooksQuery,
} from "./hooks";
import type {
  ApplyPlaybookResponse,
  PlaybookActivityBlock,
  PlaybookDetail,
  PlaybookSummary,
} from "./types";


// ── Props ──────────────────────────────────────────────────────────


export interface ApplyPlaybookSingleProps {
  mode?: "single";
  opportunity: {
    opportunity_id: string;
    renewal_year: number | null;
    renewal_month: number | null;
  };
  /** When set, pre-selects this playbook over the list-first default.
   *  Used by the Re-apply confirmation flow to lock the choice. */
  lockedPlaybookId?: string;
  onClose: () => void;
  onSuccess: (response: ApplyPlaybookResponse) => void;
}


export interface ApplyPlaybookPickProps {
  mode: "pick";
  /** Plumbs FE-SCRM-054 sales-search results into the embedded picker.
   *  Same shape <TargetSearchPicker> already speaks. */
  fetchSearchResults: (
    q: string,
    restrictTo: readonly ("opportunity")[] | undefined,
  ) => Promise<TargetSearchResult[]>;
  lockedPlaybookId?: string;
  onClose: () => void;
  onSuccess: (response: ApplyPlaybookResponse) => void;
}


export interface ApplyPlaybookBulkProps {
  mode: "bulk";
  /** Opportunities to apply the picked playbook to. Length ≥ 1. */
  opportunityIds: string[];
  /** Optional human-readable labels for the partial-failure banner.
   *  Hosts (FE-PINV-072 bulk-action bar) typically have prospect names
   *  in hand from the list row; if omitted the banner falls back to
   *  opportunity ids. */
  opportunityLabels?: Record<string, string>;
  lockedPlaybookId?: string;
  onClose: () => void;
  onSuccess: (responses: ApplyPlaybookResponse[]) => void;
}


export type ApplyPlaybookModalProps =
  | ApplyPlaybookSingleProps
  | ApplyPlaybookPickProps
  | ApplyPlaybookBulkProps;


// ── Entry ──────────────────────────────────────────────────────────


export function ApplyPlaybookModal(
  props: ApplyPlaybookModalProps,
): JSX.Element {
  const mode = props.mode ?? "single";

  if (mode === "single") {
    // Runtime invariant — TS narrowing already enforces ``opportunity``
    // at the type level, but a stale JS call site (e.g. a host updated
    // before this contract change) would silently crash without it.
    const single = props as ApplyPlaybookSingleProps;
    if (!single.opportunity && !import.meta.env.PROD) {
      throw new Error(
        "ApplyPlaybookModal: single mode requires an opportunity prop.",
      );
    }
    return <SingleModeBody {...single} />;
  }

  if (mode === "pick") {
    return <PickModeBody {...(props as ApplyPlaybookPickProps)} />;
  }

  return <BulkModeBody {...(props as ApplyPlaybookBulkProps)} />;
}


// ── Shared selection (pin-default + locked-id) ────────────────────


function usePlaybookSelection(lockedPlaybookId: string | undefined) {
  const auth = useContext(AuthContext);
  const list = usePlaybooksQuery();
  const brokerSettingsQ = useBrokerSettingsQuery(
    auth?.currentEntityId ?? null,
  );
  const playbooks: PlaybookSummary[] = list.data?.rows ?? [];
  const pinnedDefaultId =
    brokerSettingsQ.data?.pinned_default_playbook_id ?? null;

  const [selectedId, setSelectedId] = useState<string | null>(
    lockedPlaybookId ?? null,
  );

  useEffect(() => {
    if (selectedId !== null || playbooks.length === 0) return;
    if (lockedPlaybookId) {
      setSelectedId(lockedPlaybookId);
      return;
    }
    const pinnedExists = pinnedDefaultId
      && playbooks.some((p) => p.playbook_id === pinnedDefaultId);
    setSelectedId(
      pinnedExists ? pinnedDefaultId : playbooks[0]!.playbook_id,
    );
  }, [playbooks, selectedId, lockedPlaybookId, pinnedDefaultId]);

  const detail = usePlaybookDetailQuery(selectedId);

  return {
    playbooks,
    listLoading: list.isLoading,
    selectedId,
    setSelectedId,
    detail: detail.data ?? null,
    detailLoading: detail.isLoading,
    locked: Boolean(lockedPlaybookId),
  };
}


// ── mode='single' ──────────────────────────────────────────────────


function SingleModeBody({
  opportunity, lockedPlaybookId, onClose, onSuccess,
}: ApplyPlaybookSingleProps): JSX.Element {
  const sel = usePlaybookSelection(lockedPlaybookId);
  const apply = useApplyPlaybookMutation(opportunity.opportunity_id);

  const canApply =
    sel.selectedId !== null
    && opportunity.renewal_year !== null
    && opportunity.renewal_month !== null
    && !apply.isPending;

  function doApply() {
    if (sel.selectedId === null) return;
    apply.mutate(
      { playbook_id: sel.selectedId },
      {
        onSuccess: (res) => {
          onSuccess(res);
          onClose();
        },
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Apply playbook"
      size="lg"
      footer={
        <ModalFooter
          submitting={apply.isPending}
          canApply={canApply}
          onCancel={onClose}
          onApply={doApply}
        />
      }
    >
      <div
        data-testid="apply-playbook-modal"
        className="grid grid-cols-[1fr_1fr] gap-4"
      >
        <PlaybookList
          rows={sel.playbooks}
          isLoading={sel.listLoading}
          selectedId={sel.selectedId}
          onSelect={sel.setSelectedId}
          locked={sel.locked}
        />
        <PreviewPane
          detail={sel.detail}
          isLoading={sel.detailLoading}
          renewalYear={opportunity.renewal_year}
          renewalMonth={opportunity.renewal_month}
          showDueDates={true}
          applyError={apply.isError ? formatErr(apply.error) : null}
        />
      </div>
    </Modal>
  );
}


// ── mode='pick' ────────────────────────────────────────────────────


function PickModeBody({
  fetchSearchResults, lockedPlaybookId, onClose, onSuccess,
}: ApplyPlaybookPickProps): JSX.Element {
  const sel = usePlaybookSelection(lockedPlaybookId);
  const [picked, setPicked] = useState<TargetSearchResult | null>(null);
  const pickedDetailQ = useOpportunityDetailQuery(picked?.id);
  const pickedOpp = pickedDetailQ.data ?? null;
  const { applyForOpportunity, isPending } = useApplyPlaybookForOpportunity();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const restrictTo = useMemo(() => ["opportunity"] as const, []);
  const fetchAdapter = useCallback(
    (q: string, _restrictTo?: readonly ("prospect"
      | "opportunity" | "contact" | "carrier")[]) =>
      fetchSearchResults(q, restrictTo),
    [fetchSearchResults, restrictTo],
  );

  const renewalYear = pickedOpp?.renewal_year ?? null;
  const renewalMonth = pickedOpp?.renewal_month ?? null;

  const canApply =
    sel.selectedId !== null
    && picked !== null
    && renewalYear !== null
    && renewalMonth !== null
    && !isPending;

  async function doApply() {
    if (sel.selectedId === null || picked === null) return;
    setSubmitError(null);
    try {
      const res = await applyForOpportunity({
        opportunity_id: picked.id,
        playbook_id: sel.selectedId,
      });
      onSuccess(res);
      onClose();
    } catch (err) {
      setSubmitError(formatErr(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Apply playbook"
      size="lg"
      footer={
        <ModalFooter
          submitting={isPending}
          canApply={canApply}
          onCancel={onClose}
          onApply={doApply}
        />
      }
    >
      <div
        data-testid="apply-playbook-modal"
        className="flex flex-col gap-3"
      >
        <div data-testid="apply-playbook-picker">
          <label
            htmlFor="field-pick-opportunity"
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            Apply to which opportunity?
          </label>
          <TargetSearchPicker
            value={picked}
            onChange={setPicked}
            fetchResults={fetchAdapter}
            restrictTo={restrictTo}
            placeholder="Search opportunities…"
          />
        </div>
        <div className="grid grid-cols-[1fr_1fr] gap-4">
          <PlaybookList
            rows={sel.playbooks}
            isLoading={sel.listLoading}
            selectedId={sel.selectedId}
            onSelect={sel.setSelectedId}
            locked={sel.locked}
          />
          <PreviewPane
            detail={sel.detail}
            isLoading={sel.detailLoading}
            renewalYear={renewalYear}
            renewalMonth={renewalMonth}
            showDueDates={picked !== null}
            applyError={submitError}
            pickPlaceholder={picked === null}
          />
        </div>
      </div>
    </Modal>
  );
}


// ── mode='bulk' ────────────────────────────────────────────────────


interface BulkFailure {
  opportunity_id: string;
  label: string;
  reason: string;
}


function BulkModeBody({
  opportunityIds, opportunityLabels,
  lockedPlaybookId, onClose, onSuccess,
}: ApplyPlaybookBulkProps): JSX.Element {
  const sel = usePlaybookSelection(lockedPlaybookId);
  const { applyForOpportunity, isPending } = useApplyPlaybookForOpportunity();
  const [submitting, setSubmitting] = useState(false);
  const [failures, setFailures] = useState<BulkFailure[]>([]);

  const canApply =
    sel.selectedId !== null
    && opportunityIds.length > 0
    && !submitting
    && !isPending;

  function labelFor(id: string): string {
    return opportunityLabels?.[id] ?? id;
  }

  async function doApply() {
    if (sel.selectedId === null) return;
    setSubmitting(true);
    setFailures([]);
    const successes: ApplyPlaybookResponse[] = [];
    const errs: BulkFailure[] = [];
    for (const oppId of opportunityIds) {
      try {
        const res = await applyForOpportunity({
          opportunity_id: oppId,
          playbook_id: sel.selectedId,
        });
        successes.push(res);
      } catch (err) {
        errs.push({
          opportunity_id: oppId,
          label: labelFor(oppId),
          reason: formatErr(err),
        });
      }
    }
    setSubmitting(false);
    if (errs.length === 0) {
      onSuccess(successes);
      onClose();
      return;
    }
    setFailures(errs);
  }

  const bannerErrors = failures.map((f) => ({
    message: `Failed: ${f.label} — ${f.reason}`,
  }));

  return (
    <Modal
      open
      onClose={onClose}
      title="Apply playbook"
      size="lg"
      footer={
        <ModalFooter
          submitting={submitting || isPending}
          canApply={canApply}
          onCancel={onClose}
          onApply={doApply}
        />
      }
    >
      <div
        data-testid="apply-playbook-modal"
        className="flex flex-col gap-3"
      >
        <header
          data-testid="apply-playbook-bulk-header"
          className="rounded-md border border-border-subtle bg-recessed px-3 py-2 text-sm text-text-primary"
        >
          Apply to {opportunityIds.length} opportunit
          {opportunityIds.length === 1 ? "y" : "ies"}
        </header>
        {bannerErrors.length > 0 ? (
          <CrmFormBanner errors={bannerErrors} />
        ) : null}
        <div className="grid grid-cols-[1fr_1fr] gap-4">
          <PlaybookList
            rows={sel.playbooks}
            isLoading={sel.listLoading}
            selectedId={sel.selectedId}
            onSelect={sel.setSelectedId}
            locked={sel.locked}
          />
          <PreviewPane
            detail={sel.detail}
            isLoading={sel.detailLoading}
            renewalYear={null}
            renewalMonth={null}
            showDueDates={false}
            applyError={null}
            bulkCount={opportunityIds.length}
          />
        </div>
      </div>
    </Modal>
  );
}


// ── Sub-components ─────────────────────────────────────────────────


function ModalFooter({
  submitting, canApply, onCancel, onApply,
}: {
  submitting: boolean;
  canApply: boolean;
  onCancel: () => void;
  onApply: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      <Button
        variant="primary"
        onClick={onApply}
        disabled={!canApply}
        data-testid="apply-playbook-submit"
      >
        {submitting ? "Applying…" : "Apply"}
      </Button>
    </div>
  );
}


function PlaybookList({
  rows, isLoading, selectedId, onSelect, locked,
}: {
  rows: PlaybookSummary[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  locked: boolean;
}) {
  if (isLoading) {
    return (
      <div
        data-testid="playbook-row-list"
        className="rounded-md border border-border-subtle bg-raised p-3"
      >
        <p className="text-xs text-text-tertiary">Loading playbooks…</p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div
        data-testid="playbook-row-list"
        className="rounded-md border border-border-subtle bg-raised p-3"
      >
        <p className="text-xs text-text-tertiary">
          No active playbooks for this entity.
        </p>
      </div>
    );
  }
  return (
    <ul
      data-testid="playbook-row-list"
      className="flex flex-col gap-2"
    >
      {rows.map((row) => (
        <PlaybookListRow
          key={row.playbook_id}
          row={row}
          selected={row.playbook_id === selectedId}
          disabled={locked && row.playbook_id !== selectedId}
          onSelect={() => onSelect(row.playbook_id)}
        />
      ))}
    </ul>
  );
}


function PlaybookListRow({
  row, selected, disabled, onSelect,
}: {
  row: PlaybookSummary;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        data-testid={`playbook-row-${row.playbook_id}`}
        onClick={onSelect}
        disabled={disabled}
        aria-pressed={selected}
        className={[
          "w-full rounded-md border px-3 py-2 text-left transition",
          selected
            ? "border-teal bg-teal-tint"
            : "border-border-subtle bg-raised hover:border-border-default",
          disabled ? "opacity-60 cursor-not-allowed" : "",
        ].join(" ")}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-text-primary">
            {row.name}
          </span>
          {row.is_system ? (
            <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
              System
            </span>
          ) : null}
        </div>
        {row.description ? (
          <p className="mt-1 text-xs text-text-secondary">
            {row.description}
          </p>
        ) : null}
      </button>
    </li>
  );
}


function PreviewPane({
  detail, isLoading, renewalYear, renewalMonth,
  showDueDates, applyError, pickPlaceholder, bulkCount,
}: {
  detail: PlaybookDetail | null;
  isLoading: boolean;
  renewalYear: number | null;
  renewalMonth: number | null;
  /** When false, blocks render without computed due dates. Set by
   * bulk mode (one anchor per op, none to display) and by pick mode
   * pre-pick (no op yet). */
  showDueDates: boolean;
  applyError: string | null;
  pickPlaceholder?: boolean;
  bulkCount?: number;
}) {
  const anchorMissing =
    showDueDates && (renewalYear === null || renewalMonth === null);
  const blocks = detail?.activity_blocks ?? [];
  const offsets = useMemo(
    () => blocks.map((b) => b.target_offset_days_from_renewal),
    [blocks],
  );
  const summary = useMemo(() => {
    if (!showDueDates || anchorMissing || blocks.length === 0) return null;
    const firstDue = computeDueDate(
      renewalYear, renewalMonth, Math.min(...offsets),
    );
    return {
      stepCount: blocks.length,
      firstDue,
      window: durationWindowLabel(offsets),
    };
  }, [
    blocks.length, offsets, anchorMissing, renewalYear,
    renewalMonth, showDueDates,
  ]);

  return (
    <div
      data-testid="apply-playbook-preview"
      className="rounded-md border border-border-subtle bg-raised p-3"
    >
      {pickPlaceholder ? (
        <p
          data-testid="apply-playbook-pick-prompt"
          className="text-xs text-text-secondary"
        >
          Pick an opportunity above to preview computed due dates.
        </p>
      ) : null}

      {anchorMissing ? (
        <p
          data-testid="apply-playbook-anchor-missing"
          className="text-xs text-text-secondary"
        >
          Set the renewal month and year on this opportunity to
          preview computed due dates.
        </p>
      ) : null}

      {detail ? (
        <header className="mb-2">
          <h3 className="text-sm font-semibold text-text-primary">
            {detail.name}
          </h3>
          {summary ? (
            <p className="text-xs text-text-secondary">
              Applying this will create {summary.stepCount} activities
              {summary.firstDue
                ? `, first due ${summary.firstDue}`
                : ""}
              {summary.window ? ` (${summary.window}).` : "."}
            </p>
          ) : null}
          {!showDueDates && bulkCount !== undefined && blocks.length > 0 ? (
            <p className="text-xs text-text-secondary">
              Applying this will create {blocks.length} activities per
              opportunity ({blocks.length * bulkCount} total) — each
              dated against that opportunity's renewal anchor.
            </p>
          ) : null}
        </header>
      ) : isLoading ? (
        <p className="text-xs text-text-tertiary">Loading preview…</p>
      ) : null}

      {blocks.length > 0 ? (
        <ol
          data-testid="apply-playbook-preview-blocks"
          className="flex flex-col gap-1 text-xs"
        >
          {blocks.map((b) => (
            <PreviewRow
              key={b.block_id}
              block={b}
              renewalYear={renewalYear}
              renewalMonth={renewalMonth}
              showDueDates={showDueDates && !anchorMissing}
            />
          ))}
        </ol>
      ) : null}

      {applyError ? (
        <p
          data-testid="apply-playbook-error"
          className="mt-3 rounded-md border border-ember-tint bg-ember-tint p-2 text-xs text-ember"
        >
          {applyError}
        </p>
      ) : null}
    </div>
  );
}


function PreviewRow({
  block, renewalYear, renewalMonth, showDueDates,
}: {
  block: PlaybookActivityBlock;
  renewalYear: number | null;
  renewalMonth: number | null;
  showDueDates: boolean;
}) {
  const due = showDueDates
    ? computeDueDate(
        renewalYear, renewalMonth, block.target_offset_days_from_renewal,
      )
    : null;
  return (
    <li className="flex items-baseline justify-between gap-3 rounded border border-border-subtle bg-recessed px-2 py-1">
      <span className="text-text-primary">
        <span className="mr-1 text-text-tertiary">{block.ordinal}.</span>
        {block.name}
      </span>
      <time className="font-mono text-text-secondary">
        {showDueDates ? (due ?? "—") : ""}
      </time>
    </li>
  );
}


function formatErr(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "Could not apply playbook.";
}
